import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  MySubdivisionDto,
  SubdivisionStatsDto,
  Paginated,
  RequisitionDto,
  RequisitionStatus,
  CreateStoreroomInput,
  MoveLotInput,
  StoreroomDto,
  UpdateStoreroomInput,
} from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { useAuthStore } from '../../auth/model/auth.store.js';
import { requisitionApi } from '../../requisition/api/requisition.api.js';
import {
  subdivisionApi,
  type subdivisionMovementPageSchema,
  type subdivisionStockPageSchema,
} from '../api/subdivision.api.js';
import type { z } from 'zod';

type StockPage = z.infer<typeof subdivisionStockPageSchema>;
type MovementPage = z.infer<typeof subdivisionMovementPageSchema>;

/**
 * «MENING OMBORIM» so'rovlari.
 *
 * Kalitlarda bo'linma `id` si YO'Q va bu ataylab: uni server o'zi
 * aniqlaydi. `id` kalitga qo'shilsa, mijoz uni qayerdandir bilishi kerak
 * bo'lardi — bugun bilmaydi va bilishi ham shart emas.
 */
export const mySubdivisionKeys = {
  root: ['subdivision', 'mine'] as const,
  stats: ['subdivision', 'mine', 'stats'] as const,
  stock: (search: string, storeroomId: string | null) =>
    ['subdivision', 'mine', 'stock', storeroomId ?? 'all', search] as const,
  storerooms: ['subdivision', 'mine', 'storerooms'] as const,
  movements: (page: number) => ['subdivision', 'mine', 'movements', page] as const,
  requisitions: (page: number, status: RequisitionStatus | 'all') =>
    ['subdivision', 'mine', 'requisitions', status, page] as const,
};

/**
 * Xodimning O'Z bo'linmasi — bo'lim biriktirilmagan bo'lsa 404.
 *
 * Xato NORMAL natija: xodimning hammasi ham bo'limga biriktirilmagan
 * (skladchi, buxgalter, superadmin). Shuning uchun qayta urinish o'chirilgan
 * — 404 dan keyin uchta bekor so'rov yuborishning ma'nosi yo'q — va menyu
 * shu javobga qarab bo'limni ko'rsatadi.
 *
 * `enabled`: sessiya YO'Q bo'lsa so'rov umuman ketmaydi. Aks holda kirish
 * sahifasida ham 401 uchun bir so'rov uchardi.
 */
export const useMySubdivision = (): UseQueryResult<MySubdivisionDto, ApiClientError> => {
  const signedIn = useAuthStore((state) => state.accessToken !== null);

  return useQuery<MySubdivisionDto, ApiClientError>({
    queryKey: mySubdivisionKeys.root,
    queryFn: () => subdivisionApi.mine(),
    enabled: signedIn,
    retry: false,
    /* Bo'lim va uning ko'rsatkichlari kun davomida deyarli o'zgarmaydi;
       yangi kirim kelganda sahifaning o'zi kalitni bekor qiladi. */
    staleTime: 5 * 60_000,
  });
};

/**
 * PANEL KO'RSATKICHLARI.
 *
 * `useMySubdivision` dan alohida so'rov: panelda oltita yig'ma hisob bor
 * va ular sahifa ochilganda bir marta kerak. Menyu esa `mine` ni har
 * sahifada chaqiradi — ikkalasi birlashtirilsa, har o'tishda oltita
 * hisob qayta yurardi.
 *
 * Kirim yoki rasxoddan keyin sahifaning o'zi kalitni bekor qiladi.
 */
export const useMyStats = (): UseQueryResult<SubdivisionStatsDto, ApiClientError> => {
  const signedIn = useAuthStore((state) => state.accessToken !== null);

  return useQuery<SubdivisionStatsDto, ApiClientError>({
    queryKey: mySubdivisionKeys.stats,
    queryFn: () => subdivisionApi.mineStats(),
    enabled: signedIn,
    retry: false,
    staleTime: 60_000,
  });
};

export const useMyStock = (
  search: string,
  storeroomId: string | null = null,
): UseQueryResult<StockPage, ApiClientError> =>
  useQuery<StockPage, ApiClientError>({
    queryKey: mySubdivisionKeys.stock(search, storeroomId),
    queryFn: () =>
      subdivisionApi.mineStock({ search, limit: 200, ...(storeroomId === null ? {} : { storeroomId }) }),
    retry: false,
    /* Qidiruv paytida jadval BO'SHAB QOLMAYDI: yangi javob kelguncha eskisi
       turadi va ro'yxat har harfda sakramaydi. */
    placeholderData: (previous) => previous,
  });

/** O'z bo'linmasining omborlari — qabul oynasi ham, qoldiq ham shu ro'yxatdan o'qiydi. */
export const useMyStorerooms = (enabled = true): UseQueryResult<StoreroomDto[], ApiClientError> =>
  useQuery<StoreroomDto[], ApiClientError>({
    queryKey: mySubdivisionKeys.storerooms,
    queryFn: () => subdivisionApi.storerooms(),
    enabled,
    retry: false,
  });

export const useMyMovements = (page: number): UseQueryResult<MovementPage, ApiClientError> =>
  useQuery<MovementPage, ApiClientError>({
    queryKey: mySubdivisionKeys.movements(page),
    queryFn: () => subdivisionApi.mineMovements({ page, limit: 50 }),
    retry: false,
    placeholderData: (previous) => previous,
  });

/**
 * BO'LINMANING TALABNOMALARI — «nima so'ragandik va u qayerda qoldi?».
 *
 * Jurnaldan farqi: jurnal FAKTNI yozadi (nima kelib tushdi), bu esa
 * NIYATNI ko'rsatadi — hali yo'lda turgan, tasdiq kutayotgan yoki rad
 * etilgan talabnomalar ham shu yerda. Jurnal ularni hech qachon
 * ko'rsatmaydi, chunki ular omborga hali kirmagan.
 *
 * So'rov `requisition` kontekstiga ketadi (`/requisitions/subdivision/mine`)
 * — bo'linma konteksti talabnomani bilmaydi va bilmasligi ham kerak.
 */
export const useMyRequisitions = (
  page: number,
  status?: RequisitionStatus,
): UseQueryResult<Paginated<RequisitionDto>, ApiClientError> =>
  useQuery<Paginated<RequisitionDto>, ApiClientError>({
    queryKey: mySubdivisionKeys.requisitions(page, status ?? 'all'),
    queryFn: () => requisitionApi.mineSubdivision(page, 20, status),
    retry: false,
    placeholderData: (previous) => previous,
  });

/**
 * QABUL QILINMAGAN talabnomalar SONI — sarlavha panelidagi ogohlantirish.
 *
 * Ro'yxatdan sanab bo'lmaydi: u sahifalanadi va ikkinchi sahifadagi
 * kutayotgan hujjat sanoqqa tushmay qolardi. Shuning uchun alohida so'rov,
 * `limit: 1` bilan — kerak bo'lgan narsa faqat `total`.
 *
 * `HANDED_OVER` — mahsulot omborni tark etgan, lekin bo'linma hali qabul
 * qilmagan holat. Aynan shu qadam xodimni kutadi.
 */
export const usePendingReceipts = (): UseQueryResult<
  Paginated<RequisitionDto>,
  ApiClientError
> =>
  useQuery<Paginated<RequisitionDto>, ApiClientError>({
    queryKey: ['subdivision', 'mine', 'pending-receipts'] as const,
    queryFn: () => requisitionApi.mineSubdivision(1, 1, 'HANDED_OVER'),
    retry: false,
  });

/*
 * OMBOR AMALLARI — har biri `['subdivision', 'mine']` ni bekor qiladi:
 * ombor, qoldiq va jurnal bir-biriga bog'liq (ko'chirish qoldiqdagi joyni
 * ham, jurnalni ham o'zgartiradi), ya'ni bitta kalit hammasini yangilaydi.
 */
const useMineMutation = <TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, ApiClientError, TInput> => {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiClientError, TInput>({
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mySubdivisionKeys.root }),
  });
};

export const useCreateStoreroom = () =>
  useMineMutation((input: CreateStoreroomInput) => subdivisionApi.createStoreroom(input));

export const useUpdateStoreroom = () =>
  useMineMutation(({ id, input }: { id: string; input: UpdateStoreroomInput }) =>
    subdivisionApi.updateStoreroom(id, input),
  );

export const useAddZone = () =>
  useMineMutation(({ id, name }: { id: string; name: string }) => subdivisionApi.addZone(id, name));

export const useRemoveZone = () =>
  useMineMutation(({ id, zoneId }: { id: string; zoneId: string }) => subdivisionApi.removeZone(id, zoneId));

export const useMoveLot = () =>
  useMineMutation(({ lotId, input }: { lotId: string; input: MoveLotInput }) =>
    subdivisionApi.moveLot(lotId, input),
  );
